// Dependency-free in-app lightbox for markdown images (Obsidian idiom).
// Mounted ONCE (in Chat.tsx) and opened via a window CustomEvent so the
// dangerouslySetInnerHTML-injected <img> tags can trigger it through simple
// click delegation on the .pa-body wrapper - no inline JS in generated HTML.
//
//   window.dispatchEvent(new CustomEvent('pa:lightbox', {
//     detail: { src, alt, el /* thumbnail element, for focus return */ }
//   }))
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { themeColors } from '../../theme/colors';

export interface LightboxOpenDetail {
  src: string;
  alt?: string;
  el?: HTMLElement | null;
}

const ImageLightbox: React.FC<{ theme: 'light' | 'dark' }> = ({ theme }) => {
  const c = themeColors[theme];
  const [data, setData] = useState<{ src: string; alt: string } | null>(null);
  const [entered, setEntered] = useState(false);
  const openerRef = useRef<HTMLElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  // Listen for open events
  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<LightboxOpenDetail>).detail;
      if (!detail?.src) return;
      openerRef.current = detail.el || null;
      setData({ src: detail.src, alt: detail.alt || '' });
    };
    window.addEventListener('pa:lightbox', onOpen);
    return () => window.removeEventListener('pa:lightbox', onOpen);
  }, []);

  // Enter animation + scroll lock + initial focus
  useEffect(() => {
    if (!data) return;
    const raf = requestAnimationFrame(() => setEntered(true));
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtnRef.current?.focus();
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = prevOverflow;
    };
  }, [data]);

  const close = useCallback(() => {
    setData(null);
    setEntered(false);
    // Return focus to the thumbnail that opened us
    const opener = openerRef.current;
    if (opener) {
      if (!opener.hasAttribute('tabindex')) opener.setAttribute('tabindex', '-1');
      opener.focus({ preventScroll: false });
    }
  }, []);

  // Esc closes
  useEffect(() => {
    if (!data) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [data, close]);

  if (!data) return null;

  const reducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const ghostBtn: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    fontFamily: "'Inter', system-ui, sans-serif",
    fontSize: '11.5px',
    fontWeight: 500,
    color: '#9FA4AD',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    padding: '5px 11px',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'background 150ms ease, color 150ms ease',
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={data.alt || 'Gambar'}
      onClick={close}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 4000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(6,7,9,0.82)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        opacity: entered ? 1 : 0,
        transition: 'opacity 200ms ease-out',
      }}
    >
      {/* Close (ghost, top-right) */}
      <button
        ref={closeBtnRef}
        onClick={close}
        aria-label="Tutup"
        title="Tutup (Esc)"
        style={{
          position: 'absolute',
          top: '18px',
          right: '20px',
          width: '34px',
          height: '34px',
          borderRadius: '9px',
          display: 'grid',
          placeItems: 'center',
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.12)',
          color: '#9FA4AD',
          fontSize: '15px',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.10)'; e.currentTarget.style.color = '#F2F3F5'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#9FA4AD'; }}
      >
        ✕
      </button>

      {/* Content (clicks inside must not close) */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          maxWidth: '90vw',
          transform: reducedMotion ? 'none' : (entered ? 'scale(1)' : 'scale(0.96)'),
          transition: reducedMotion ? 'none' : 'transform 200ms ease-out',
        }}
      >
        <img
          src={data.src}
          alt={data.alt}
          style={{
            maxWidth: '90vw',
            maxHeight: '85vh',
            width: 'auto',
            height: 'auto',
            borderRadius: '16px',
            boxShadow: '0 40px 90px -20px rgba(0,0,0,0.8)',
            display: 'block',
          }}
        />
        {data.alt && (
          <div style={{
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: '12px',
            color: '#9FA4AD',
            textAlign: 'center',
            maxWidth: '70vw',
          }}>
            {data.alt}
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px' }}>
          <a
            href={data.src}
            target="_blank"
            rel="noopener noreferrer"
            style={ghostBtn}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.10)'; e.currentTarget.style.color = c.ink; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#9FA4AD'; }}
          >
            Buka penuh ↗
          </a>
          <a
            href={data.src}
            download
            style={ghostBtn}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.10)'; e.currentTarget.style.color = c.ink; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#9FA4AD'; }}
          >
            Unduh
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImageLightbox;
